"use client";

import { IForm, IUserData } from "@/types";
import { Label } from "@/components/ui/label";
import { TextField } from "./fields/Text";
import { TextArea } from "./fields/Textarea";
import { CheckboxField } from "./fields/Checkbox";
import { DropdownField } from "./fields/Dropdown";
import { useForm, FormProvider } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { IconLoader, IconLoader2 } from "@tabler/icons-react";
import HCaptcha from "@hcaptcha/react-hcaptcha";

import {
    cn,
    getTextColorForBackground,
    rgbToHex,
    colorToHsl,
} from "@/lib/utils";
import { UserURL } from "@/functions";
import { Separator } from "@/components/ui/separator";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Turnstile } from "@marsidev/react-turnstile";
import { TextOnlyField } from "./fields/TextOnly";

interface Props {
    account: IUserData;
    form: IForm;
    admin?: boolean;
    setSelectedField?: (field: any) => void;
    selectedField?: any;
    type?: "page" | "editor" | "dialog";
}

// ✅ Dynamically Generate Zod Schema
const generateSchema = (fields: IForm["fields"]) => {
    let schemaShape: Record<string, any> = {};

    fields.forEach((field) => {
        switch (field.type) {
            case "text":
            case "textarea":
                schemaShape[field.id] = field.required
                    ? z.string().min(1, `${field.question} is required`)
                    : z.string().optional();
                break;

            case "checkbox":
                schemaShape[field.id] = field.required
                    ? z.boolean().refine((val) => val === true, {
                          message: "This field is required",
                      })
                    : z.boolean().optional();
                break;

            case "dropdown":
                schemaShape[field.id] = field.required
                    ? z.string().min(1, `${field.question} is required`)
                    : z.string().optional();
                break;

            default:
                break;
        }
    });

    return z.object(schemaShape);
};

export default function FormRender({
    account,
    form,
    admin,
    setSelectedField,
    selectedField,
    type,
}: Props) {
    const captchaRef = useRef<HCaptcha>(null);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const FormSchema = generateSchema(form.fields);

    const methods = useForm({
        resolver: zodResolver(FormSchema),
        defaultValues: form.fields.reduce(
            (acc, field) => {
                acc[field.id] = field.type === "checkbox" ? false : "";
                return acc;
            },
            {} as Record<string, any>
        ),
    });
    const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);

    const onSubmit = async (data: any) => {
        // Validate that captcha token exists
        if (!captchaToken) {
            toast.error("Please complete the captcha");
            return;
        }

        setIsSubmitting(true);
        const req = await fetch("/api/form/submission", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                id: form.id,
                account_id: account.id,
                answers: data,
                captcha_token: captchaToken,
            }),
        }).then((res) => res.json());

        if (req.message === "success") {
            setIsSubmitting(false);
            setIsSubmitSuccess(true);
            toast.success("Form submitted successfully");
            // Reset captcha after successful submission
            captchaRef.current?.resetCaptcha();
            setCaptchaToken(null);
        } else {
            toast.error("Something went wrong");
            setIsSubmitting(false);
            // Reset captcha on error
            captchaRef.current?.resetCaptcha();
            setCaptchaToken(null);
        }
    };
    let BackgroundStyle: any = {};
    if (
        account?.theme.background_img &&
        account?.theme?.background_img.length != 0
    ) {
        BackgroundStyle.backgroundImage = `url(${account?.theme.background_img})`;
        BackgroundStyle.backgroundSize = "cover";
        BackgroundStyle.backgroundPosition = "center";
        // BackgroundStyle.backgroundAttachment = "fixed";
    } else {
        BackgroundStyle.background = account?.theme.background;
        // BackgroundStyle.backgroundAttachment = "fixed"; // Add this line
    }
    if (!admin && !form.published) {
        return (
            <div className="mx-auto flex h-full w-full max-w-prose flex-col place-content-center gap-1">
                <Label variant={"headingtoggle"}>Hmmm....</Label>
                <Label variant={"toggle"}>
                    The form you're trying to access is no longer available. It
                    has likely been unpublished or deleted.
                </Label>
                <div className="flex items-center gap-3 py-3">
                    <a href={`https://${UserURL(account)}`}>
                        <Button>Go back</Button>
                    </a>
                </div>
            </div>
        );
    }
    return (
        <FormProvider {...methods}>
            <style>
                {`:root { 
                --form-background-color: ${colorToHsl(form.theme.background)}; 
                --form-primary-color: ${colorToHsl(form.theme.primary)};
                --form-primary-text-color: ${colorToHsl(form.theme.text)};
                }}`}
            </style>
            <div className="h-full w-full overflow-auto bg-form-background-color">
                <form
                    onSubmit={methods.handleSubmit(onSubmit)}
                    className={cn(
                        "flex w-full max-w-lg flex-col gap-3 rounded-md bg-form-background-color p-4",
                        type === "page" && " mx-auto w-full max-w-6xl",
                        !type && "min-w-full p-0"
                    )}
                >
                    <div className="flex w-full flex-col gap-1">
                        <div className="flex w-full flex-col gap-0">
                            {form.name ? (
                                <Label
                                    variant="pageHeading"
                                    className={cn(
                                        "text-form-primary-text-color",
                                        type === "page" && "text-4xl"
                                    )}
                                >
                                    {form.name}
                                </Label>
                            ) : (
                                <Label
                                    variant="pageHeading"
                                    className="text-form-primary-text-color/50"
                                >
                                    Unamed Form
                                </Label>
                            )}
                            <Label
                                variant="pageHeading"
                                className="text-lg font-medium text-form-primary-text-color"
                            >
                                {form.description}
                            </Label>
                        </div>
                        {/* Header */}
                        <a
                            target="_blank"
                            href={"https://" + UserURL(account)}
                            className={cn("flex w-fit flex-col gap-1")}
                        >
                            <Label
                                className="cursor-pointer font-bold text-form-primary-text-color"
                                variant={"subtoggle"}
                            >
                                Created by
                            </Label>
                            <div
                                className={cn(
                                    "flex items-center gap-2",
                                    "bg-form-primary-color p-2 text-form-primary-text-color",
                                    account.theme.button_rounded ==
                                        "rounded-xl" ||
                                        account.theme.button_rounded ==
                                            "rounded-full"
                                        ? "rounded-md"
                                        : account.theme.button_rounded
                                )}
                            >
                                <img
                                    src={account.pic}
                                    className="h-6 w-6 rounded-full"
                                    alt="Profile Picture"
                                />
                                <Label
                                    className="cursor-pointer font-bold text-form-primary-text-color"
                                    variant={"toggle"}
                                >
                                    {account.displayname}
                                </Label>
                            </div>
                        </a>
                    </div>
                    <div className="mt-4 flex w-full min-w-full flex-col gap-4">
                        {form.fields.length == 0 ? (
                            <div className="flex w-full flex-col gap-2">
                                <Label
                                    variant="headingtoggle"
                                    className="text-form-primary-text-color"
                                >
                                    This form is empty
                                </Label>
                                <div
                                    className="w-fit cursor-pointer rounded-md bg-form-primary-color p-2 text-sm font-bold"
                                    onClick={() => {
                                        if (admin && setSelectedField) {
                                            setSelectedField(null); // Open item when admin clicks
                                        }
                                    }}
                                >
                                    Add a field
                                </div>
                            </div>
                        ) : null}
                        {form.fields.map((field, index) => {
                            // Calculate the question number, skipping "text-only" fields
                            let questionCount = 0;
                            for (let i = 0; i <= index; i++) {
                                if (form.fields[i].type !== "text-only") {
                                    questionCount++;
                                }
                            }
                            const questionNumber = `Question ${questionCount}`;

                            const renderField = () => {
                                switch (field.type) {
                                    case "text":
                                        return (
                                            <div key={field.id}>
                                                <Label className="mb-0 text-xs font-normal text-form-primary-text-color/70">
                                                    {questionNumber}
                                                </Label>
                                                <TextField
                                                    field={field}
                                                    admin={admin}
                                                    className={cn(
                                                        "rounded-md border border-transparent p-3 text-form-primary-text-color transition-all duration-200",
                                                        selectedField?.id ===
                                                            field.id
                                                            ? admin
                                                                ? "border-primary"
                                                                : ""
                                                            : admin &&
                                                                  "hover:border-primary/50"
                                                    )}
                                                />
                                            </div>
                                        );
                                    case "textarea":
                                        return (
                                            <div key={field.id}>
                                                <Label className="mb-0 text-xs font-normal text-form-primary-text-color/70">
                                                    {questionNumber}
                                                </Label>
                                                <TextArea
                                                    key={field.id}
                                                    field={field}
                                                    admin={admin}
                                                    className={cn(
                                                        "rounded-md border border-transparent p-3 text-form-primary-text-color transition-all duration-200",

                                                        selectedField?.id ===
                                                            field.id
                                                            ? admin
                                                                ? "border-primary"
                                                                : ""
                                                            : admin &&
                                                                  "hover:border-primary/50"
                                                    )}
                                                />
                                            </div>
                                        );
                                    case "checkbox":
                                        return (
                                            <div key={field.id}>
                                                <Label className="mb-0 text-xs font-normal text-form-primary-text-color/70">
                                                    {questionNumber}
                                                </Label>
                                                <CheckboxField
                                                    key={field.id}
                                                    field={field}
                                                    admin={admin}
                                                    className={cn(
                                                        "rounded-md border border-transparent p-3 text-form-primary-text-color transition-all duration-200",

                                                        selectedField?.id ===
                                                            field.id
                                                            ? admin
                                                                ? "border-primary"
                                                                : ""
                                                            : admin &&
                                                                  "hover:border-primary/50"
                                                    )}
                                                />
                                            </div>
                                        );
                                    case "dropdown":
                                        return (
                                            <div key={field.id}>
                                                <Label className="mb-0 text-xs font-normal text-form-primary-text-color/70">
                                                    {questionNumber}
                                                </Label>
                                                <DropdownField
                                                    key={field.id}
                                                    field={field}
                                                    admin={admin}
                                                    className={cn(
                                                        "rounded-md border border-transparent p-3 text-form-primary-text-color transition-all duration-200",

                                                        selectedField?.id ===
                                                            field.id
                                                            ? admin
                                                                ? "border-primary"
                                                                : ""
                                                            : admin &&
                                                                  "hover:border-primary/50"
                                                    )}
                                                />
                                            </div>
                                        );
                                        case "text-only":
                                        return (
                                            <div key={field.id}>
                                                {/* No question number label for text-only fields */}
                                                <TextOnlyField
                                                    field={field}
                                                    admin={admin}
                                                    className={cn(
                                                        "rounded-md border border-transparent p-3 text-form-primary-text-color transition-all duration-200",
                                                        selectedField?.id ===
                                                            field.id
                                                            ? admin
                                                                ? "border-primary"
                                                                : ""
                                                            : admin &&
                                                                  "hover:border-primary/50"
                                                    )}
                                                />
                                            </div>
                                        );
                                    default:
                                        return null;
                                }
                            };

                            if (admin)
                                return (
                                    <div
                                        key={field.id}
                                        onClick={() => {
                                            setSelectedField &&
                                                setSelectedField(
                                                    form.fields.find(
                                                        (_field) =>
                                                            _field.id ===
                                                            field.id
                                                    )
                                                );
                                            window.dispatchEvent(
                                                new CustomEvent(
                                                    "formBuilderItemAdd",
                                                    {
                                                        detail: {
                                                            showFieldDetails:
                                                                true,
                                                        },
                                                    }
                                                )
                                            );
                                        }}
                                    >
                                        {renderField()}
                                    </div>
                                );

                            return renderField();
                        })}
                    </div>
                    <div className={cn("px-2", !type && "px-4")}>
                        <HCaptcha
                            sitekey={
                                process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY || ""
                            }
                            onVerify={(token) => setCaptchaToken(token)}
                            onExpire={() => setCaptchaToken(null)}
                            ref={captchaRef}
                            theme="dark" // or 'dark' based on your form's theme
                        />
                    </div>
                    <div

                        className={cn(
                            "flex w-full items-center gap-2 px-2",
                            !type && "px-4"
                        )}
                    >
                        {isSubmitting && (
                            <IconLoader2 className="animate-spin" />
                        )}
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    // type="submit"
                                    className={cn(
                                        "w-fit bg-form-primary-color px-8 font-bold text-form-primary-text-color transition-all duration-200 hover:scale-105 hover:bg-form-primary-color",
                                        account.theme.button_rounded
                                    )}
                                    disabled={admin || isSubmitting}
                                >
                                    Submit
                                </Button>
                            </PopoverTrigger>

                            <div className="z-[99999] flex items-center">
                                {isSubmitSuccess ? (
                                    <>
                                        <PopoverContent className="z-[99999] border-form-primary-color bg-form-background-color ">
                                            <Label
                                                variant={"headingtoggle"}
                                                className="text-form-primary-text-color"
                                            >
                                                Form submitted!
                                            </Label>
                                            <Label
                                                variant={"toggle"}
                                                className="text-form-primary-text-color"
                                            >
                                                Your form has been successfully
                                                submitted to{" "}
                                                {account.displayname}.
                                            </Label>
                                        </PopoverContent>
                                    </>
                                ) : (
                                    <>
                                        <PopoverContent className="z-[99999] border-form-primary-color bg-form-background-color">
                                            <Label
                                                variant={"headingtoggle"}
                                                className="text-form-primary-text-color"
                                            >
                                                Submit your response
                                            </Label>
                                            <p className="text-xs text-form-primary-text-color">
                                                Never include sensitive
                                                information like personal
                                                information you don't wish to
                                                share, or passwords!{" "}
                                                <span>
                                                    <br />
                                                    Everything you enter here
                                                    will be shared with{" "}
                                                    {account.displayname}.
                                                </span>
                                                <span>
                                                    <br />
                                                    <br />
                                                    Something suspicious?{" "}
                                                    <a
                                                        target="_blank"
                                                        href={`/report?user=${account.id}`}
                                                        className="w-fit underline"
                                                    >
                                                        Report it
                                                    </a>
                                                </span>
                                            </p>
                                            <Separator className="my-2" />
                                            <div className="flex w-full flex-col items-center gap-2">
                                                <Button
                                                    onClick={methods.handleSubmit(
                                                        onSubmit
                                                    )}
                                                    disabled={isSubmitting}
                                                    type="submit"
                                                    className="bg-form-primary-color font-bold hover:bg-form-primary-color/80"
                                                >
                                                    {isSubmitting ? (
                                                        <IconLoader2 className="animate-spin" />
                                                    ) : (
                                                        "Submit"
                                                    )}
                                                </Button>
                                            </div>
                                        </PopoverContent>
                                    </>
                                )}
                            </div>
                        </Popover>
                        <a
                            href={`/report?user=${account.id}`}
                            target="_blank"
                            className="text-xs font-bold underline"
                        >
                            Report
                        </a>
                    </div>
                    <div className="flex w-full flex-col gap-1 rounded-md !bg-background p-3 !text-foreground">
                        <Label variant={"toggle"}>
                            Never submit passwords or sensitive information.
                        </Label>
                        <Label variant={"subtoggle"} className="block">
                            This form is created by a user and not affiliated or
                            endorsed with Doras.{" "}
                            <a
                                href="https://doras.to/legal/tos"
                                className="underline hover:text-primary"
                                target="_blank"
                            >
                                Terms of Service
                            </a>{" "}
                            -{" "}
                            <a
                                href="https://doras.to/legal/privacy-policy"
                                className="underline hover:text-primary"
                                target="_blank"
                            >
                                Privacy Policy
                            </a>
                        </Label>
                    </div>
                </form>
            </div>
        </FormProvider>
    );
}
